import React, { Children, isValidElement } from "react";
import { useInView } from "../../hooks/useInView";

const VARIANT_CLASS = {
  fadeUp: "anim-fade-up",
  fadeIn: "anim-fade-in",
  fadeDown: "anim-fade-down",
  slideLeft: "anim-slide-left",
  slideRight: "anim-slide-right",
  scaleIn: "anim-scale-in",
  zoomIn: "anim-zoom-in",
};

/**
 * Scroll-triggered reveal animation.
 *
 * @param {"fadeUp"|"fadeIn"|"fadeDown"|"slideLeft"|"slideRight"|"scaleIn"|"zoomIn"} variant
 * @param {number} delay - ms delay before animation starts
 * @param {number} duration - ms animation duration
 * @param {string} className - extra classes on wrapper
 * @param {boolean} as - HTML tag (default "div")
 */
export function Animate({
  children,
  variant = "fadeUp",
  delay = 0,
  duration = 700,
  className = "",
  as: Tag = "div",
  threshold,
  once = true,
  style = {},
  ...rest
}) {
  const [ref, inView] = useInView({ threshold, once });
  const variantClass = VARIANT_CLASS[variant] || VARIANT_CLASS.fadeUp;

  return (
    <Tag
      ref={ref}
      className={`anim-base ${variantClass} ${inView ? "anim-visible" : ""} ${className}`}
      style={{
        transitionDelay: `${delay}ms`,
        transitionDuration: `${duration}ms`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Stagger children with cascading delay.
 */
export function Stagger({
  children,
  variant = "fadeUp",
  stagger = 100,
  baseDelay = 0,
  duration = 700,
  className = "",
  as: Tag = "div",
}) {
  const items = Children.toArray(children);

  return (
    <Tag className={className}>
      {items.map((child, index) => {
        if (!isValidElement(child)) return child;
        return (
          <Animate
            key={child.key || index}
            variant={variant}
            delay={baseDelay + index * stagger}
            duration={duration}
          >
            {child}
          </Animate>
        );
      })}
    </Tag>
  );
}

export default Animate;
